import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute, { defaultHomeFor } from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import Clientes from "@/pages/Clientes";
import Sucursales from "@/pages/Sucursales";
import Pagos from "@/pages/Pagos";
import Auditoria from "@/pages/Auditoria";

import AdminDashboard from "@/pages/AdminDashboard";
import MenuAdmin from "@/pages/MenuAdmin";
import MesasAdmin from "@/pages/MesasAdmin";
import OrdenesList from "@/pages/OrdenesList";
import OrdenDetalle from "@/pages/OrdenDetalle";
import Caja from "@/pages/Caja";
import KitchenPanel from "@/pages/KitchenPanel";
import Reparto from "@/pages/Reparto";
import Inventario from "@/pages/Inventario";
import Gastos from "@/pages/Gastos";
import Reportes from "@/pages/Reportes";
import Usuarios from "@/pages/Usuarios";
import Configuracion from "@/pages/Configuracion";
import OpcionesRapidas from "@/pages/OpcionesRapidas";
import AreasAdmin from "@/pages/AreasAdmin";

import MeseroHome from "@/pages/MeseroHome";
import NuevaOrden from "@/pages/NuevaOrden";

import PedidoPublico from "@/pages/PedidoPublico";
import SeguimientoPedido from "@/pages/SeguimientoPedido";
import Acompanamientos from "@/pages/Acompanamientos";

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={defaultHomeFor(user.role)} replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="bottom-right" duration={3000} closeButton={false} />
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/pedido" element={<PedidoPublico />} />
            <Route path="/pedido/:sucursalId" element={<PedidoPublico />} />
            <Route path="/seguimiento" element={<SeguimientoPedido />} />
            <Route path="/seguimiento/:numero" element={<SeguimientoPedido />} />

            {/* SUPER_ADMIN */}
            <Route element={<ProtectedRoute roles={["SUPER_ADMIN"]}><Layout /></ProtectedRoute>}>
              <Route path="/admin" element={<SuperAdminDashboard />} />
              <Route path="/admin/clientes" element={<Clientes />} />
              <Route path="/admin/sucursales" element={<Sucursales />} />
              <Route path="/admin/pagos" element={<Pagos />} />
              <Route path="/admin/auditoria" element={<Auditoria />} />
            </Route>

            {/* ADMIN_SUCURSAL & CAJA & REPARTIDOR (reparto consolidado) */}
            <Route element={<ProtectedRoute roles={["ADMIN_SUCURSAL", "CAJA", "REPARTIDOR"]}><Layout /></ProtectedRoute>}>
              <Route path="/reparto" element={<Reparto />} />
            </Route>

            {/* ADMIN_SUCURSAL & CAJA */}
            <Route element={<ProtectedRoute roles={["ADMIN_SUCURSAL", "CAJA"]}><Layout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<AdminDashboard />} />
              <Route path="/caja" element={<Caja />} />
              <Route path="/reportes" element={<Reportes />} />
            </Route>

            {/* Listado de Órdenes + detalle: ADMIN/CAJA con acceso completo. MESERO/COCINA/BEBIDAS/FREIDORA solo del día actual (enforced en backend). */}
            <Route element={<ProtectedRoute roles={["ADMIN_SUCURSAL", "CAJA", "MESERO", "COCINA", "BEBIDAS", "FREIDORA"]}><Layout /></ProtectedRoute>}>
              <Route path="/ordenes" element={<OrdenesList />} />
              <Route path="/orden/:id" element={<OrdenDetalle />} />
            </Route>

            <Route element={<ProtectedRoute roles={["ADMIN_SUCURSAL"]}><Layout /></ProtectedRoute>}>
              <Route path="/menu" element={<MenuAdmin />} />
              <Route path="/acompanamientos" element={<Acompanamientos />} />
              <Route path="/areas" element={<AreasAdmin />} />
              <Route path="/opciones-rapidas" element={<OpcionesRapidas />} />
              <Route path="/mesas" element={<MesasAdmin />} />
              <Route path="/inventario" element={<Inventario />} />
              <Route path="/gastos" element={<Gastos />} />
              <Route path="/usuarios" element={<Usuarios />} />
              <Route path="/configuracion" element={<Configuracion />} />
            </Route>

            {/* MESERO + ADMIN_SUCURSAL pueden levantar órdenes */}
            <Route element={<ProtectedRoute roles={["MESERO", "ADMIN_SUCURSAL"]}><Layout /></ProtectedRoute>}>
              <Route path="/mesero" element={<MeseroHome />} />
              <Route path="/mesero/nueva" element={<NuevaOrden />} />
              <Route path="/mesero/orden/:id" element={<OrdenDetalle />} />
            </Route>

            {/* COCINA / BEBIDAS / FREIDORA — modo legacy single-área */}
            <Route element={<ProtectedRoute roles={["COCINA", "ADMIN_SUCURSAL"]}><Layout /></ProtectedRoute>}>
              <Route path="/cocina" element={<KitchenPanel area="COCINA" />} />
            </Route>
            <Route element={<ProtectedRoute roles={["BEBIDAS", "ADMIN_SUCURSAL"]}><Layout /></ProtectedRoute>}>
              <Route path="/bebidas" element={<KitchenPanel area="BEBIDAS" />} />
            </Route>
            <Route element={<ProtectedRoute roles={["FREIDORA", "ADMIN_SUCURSAL"]}><Layout /></ProtectedRoute>}>
              <Route path="/freidora" element={<KitchenPanel area="FREIDORA" />} />
            </Route>
            {/* Vista operativa multi-área: una sola pantalla con chips por área */}
            <Route element={<ProtectedRoute roles={["COCINA", "BEBIDAS", "FREIDORA", "ADMIN_SUCURSAL"]}><Layout /></ProtectedRoute>}>
              <Route path="/mis-areas" element={<KitchenPanel />} />
            </Route>

            {/* REPARTIDOR home is /reparto - handled above */}

            <Route path="*" element={<HomeRedirect />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
